
import { Search, FileText, Car, CheckCircle, Clock, Phone, Mail, ArrowRight } from 'lucide-react';
import { IslamicSymbol } from './ui/islamic-symbol';
import { NavigationBar } from "./shared/Navigation";
import { Footer } from "./shared/Footer";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { useLanguage } from '../contexts/LanguageContext';

interface HowItWorksProps {
  onNavigate: (page: string) => void;
  onContactClick: () => void;
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
}

function HeroSection() {
  const { t } = useLanguage();
  
  return (
    <section className="bg-gradient-to-br from-[#f91bdc]/10 via-white to-[#f91bdc]/5 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span dangerouslySetInnerHTML={{ __html: t('howItWorksPage.title') }} />
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('howItWorksPage.subtitle')}
          </p>
          <div className="flex items-center justify-center space-x-4 text-gray-500">
            <div className="flex items-center space-x-2">
              <IslamicSymbol size={20} className="text-[#f91bdc]" />
              <span>{t('howItWorksPage.halal')}</span>
            </div>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <div className="flex items-center space-x-2">
              <Clock size={20} className="text-[#f91bdc]" />
              <span>{t('howItWorksPage.quickProcess')}</span>
            </div>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <div className="flex items-center space-x-2">
              <CheckCircle size={20} className="text-[#f91bdc]" />
              <span>{t('howItWorksPage.fixedPricing')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepsSection({ onNavigate }: { onNavigate: (page: string) => void }) {
  const { t } = useLanguage();
  
  const steps = [
    {
      number: "01",
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.description'),
      details: [
        t('step.browse.detail1'),
        t('step.browse.detail2'),
        t('step.browse.detail3'),
        t('step.browse.detail4')
      ],
      icon: <Search size={48} />,
      action: () => onNavigate('listings'),
      actionText: t('hero.browseCars')
    },
    {
      number: "02",
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.description'),
      details: [
        t('step.buy.detail1'),
        t('step.buy.detail2'),
        t('step.buy.detail3'),
        t('step.buy.detail4')
      ],
      icon: <FileText size={48} />,
      action: () => onNavigate('listings'),
      actionText: t('common.getStarted')
    },
    {
      number: "03",
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.description'),
      details: [
        t('step.complete.detail1'),
        t('step.complete.detail2'),
        t('step.complete.detail3'),
        t('step.complete.detail4')
      ],
      icon: <Car size={48} />,
      action: () => {},
      actionText: t('common.learnMore')
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('howItWorksPage.threeSteps')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('howItWorksPage.stepsDescription')}
          </p>
        </div>

        <div className="space-y-16">
          {steps.map((step, index) => (
            <div key={index} className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <div className="inline-flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-[#f91bdc] text-white rounded-full flex items-center justify-center text-2xl font-bold">
                    {step.number}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {step.title}
                  </h3>
                </div>
                
                <p className="text-lg text-gray-600 mb-8">
                  {step.description}
                </p>

                {/* Card positioned directly under title and before bullet points */}
                <div className="max-w-md mx-auto mb-8">
                  <Card className="border-[#f91bdc]/20 hover:border-[#f91bdc]/40 transition-all duration-300 hover:shadow-xl">
                    <CardContent className="p-12 text-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <div className="text-[#f91bdc]">
                          {step.icon}
                        </div>
                      </div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-4">
                        Step {step.number}
                      </h4>
                      <p className="text-gray-600">
                        {step.title}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="text-center lg:text-left max-w-2xl mx-auto">
                <ul className="space-y-3 mb-8">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start space-x-3">
                      <CheckCircle className="text-[#f91bdc] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-600">{detail}</span>
                    </li>
                  ))}
                </ul>
                
                {step.action && (
                  <div className="text-center">
                    <Button 
                      onClick={step.action}
                      className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-8 py-3"
                    >
                      {step.actionText} <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ShariahSection() {
  const { t } = useLanguage();
  
  const principles = [
    {
      title: t('howItWorksPage.noInterestPrinciple'),
      description: t('howItWorksPage.noInterestDescription'),
      icon: <IslamicSymbol size={24} />
    },
    {
      title: t('howItWorksPage.fixedTerms'),
      description: t('howItWorksPage.fixedTermsDescription'),
      icon: <CheckCircle size={24} />
    },
    {
      title: t('howItWorksPage.assetBacked'),
      description: t('howItWorksPage.assetBackedDescription'),
      icon: <Car size={24} />
    },
    {
      title: t('howItWorksPage.simpleApproval'),
      description: t('howItWorksPage.simpleApprovalDescription'),
      icon: <Clock size={24} />
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            <span dangerouslySetInnerHTML={{ __html: t('howItWorksPage.shariahCompliance') }} />
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('howItWorksPage.shariahDescription')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {principles.map((principle, index) => (
            <Card key={index} className="border-[#f91bdc]/20 hover:border-[#f91bdc]/40 transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#f91bdc] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    {principle.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {principle.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-[#f91bdc]/20 bg-gradient-to-r from-[#f91bdc]/5 to-[#f91bdc]/10">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#f91bdc] text-white rounded-full flex items-center justify-center mx-auto mb-6">
              <IslamicSymbol size={32} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('howItWorksPage.certifiedCompliant')}
            </h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              {t('howItWorksPage.certifiedDescription')}
            </p>
            <Badge className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-6 py-2 text-sm">
              {t('howItWorksPage.scholarApproved')}
            </Badge>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function FAQSection() {
  const { t } = useLanguage();
  
  const faqs = [
    {
      question: t('faq.q1'),
      answer: t('faq.a1')
    },
    {
      question: t('faq.q2'),
      answer: t('faq.a2')
    },
    {
      question: t('faq.q3'),
      answer: t('faq.a3')
    },
    {
      question: t('faq.q4'),
      answer: t('faq.a4')
    },
    {
      question: t('faq.q5'),
      answer: t('faq.a5')
    },
    {
      question: t('faq.q6'),
      answer: t('faq.a6')
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('howItWorksPage.faq')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('howItWorksPage.faqSubtitle')}
          </p>
        </div>

        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <Card key={index} className="border-[#f91bdc]/20">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-600 mb-6">
            {t('howItWorks.faq.helpText')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              className="border-[#f91bdc] text-[#f91bdc] hover:bg-[#f91bdc] hover:text-white"
              onClick={() => window.open('tel:+14378871015', '_self')}
            >
              <Phone size={16} className="mr-2" />
              {t('howItWorks.faq.callUs')}
            </Button>
            <Button 
              variant="outline" 
              className="border-[#f91bdc] text-[#f91bdc] hover:bg-[#f91bdc] hover:text-white"
              onClick={() => window.open('mailto:Contact@sayaara.com', '_self')}
            >
              <Mail size={16} className="mr-2" />
              {t('howItWorks.faq.emailUs')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HowItWorks({
  onNavigate,
  onContactClick,
  onOpenTerms,
  onOpenPrivacy
}: HowItWorksProps) {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-white">
      <NavigationBar 
        onContactClick={onContactClick} 
      />
      
      <HeroSection />
      <StepsSection onNavigate={onNavigate} />
      <ShariahSection />
      <FAQSection />
      
      <section className="py-16 bg-gradient-to-br from-[#f91bdc]/10 via-white to-[#f91bdc]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {t('howItWorksPage.readyToStart')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('howItWorksPage.readyDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => onNavigate('listings')}
              className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-8 py-3 text-lg"
            >
              {t('hero.browseCars')} <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={onContactClick}
              className="border-[#f91bdc] text-[#f91bdc] hover:bg-[#f91bdc] hover:text-white px-8 py-3 text-lg"
            >
              {t('common.contactUs')}
            </Button>
          </div>
        </div>
      </section>

      <Footer 
        onOpenTerms={onOpenTerms} 
        onOpenPrivacy={onOpenPrivacy} 
      />
    </div>
  );
}